import { useEffect, useMemo, useRef, useState } from "react";
import LawList from "./LawList";
import TimelineControls from "./TimelineControl";
import TimelinePoint from "./TimelinePoint";
import { Evaluaciones } from "../../../../../interfaces/Evaluations";

const Timeline = ({ laws, period }: { laws: Evaluaciones; period: string }) => {
  const timelineRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<HTMLDivElement>(null);
  const itemRef = useRef<HTMLButtonElement>(null);

  const [selectedYear, setSelectedYear] = useState<string | null>(period);
  const [showAll, setShowAll] = useState(false);
  const sortedYears = Object.keys(laws).sort(
    (a, b) => parseInt(b) - parseInt(a)
  );

  useEffect(() => {
    setSelectedYear(period);
  }, [period]);

  useEffect(() => {
    if (!selectedYear || !timelineRef.current) return;

    const container = timelineRef.current;
    const selectedEl = container.querySelector(
      `[data-year="${selectedYear}"]`
    ) as HTMLElement | null;

    if (selectedEl) {
      const containerWidth = container.clientWidth;
      const elementLeft = selectedEl.offsetLeft;
      const elementWidth = selectedEl.offsetWidth;

      const scrollTo = elementLeft - containerWidth / 2 + elementWidth / 2;
      container.scrollTo({ left: scrollTo, behavior: "smooth" });
    }
  }, [selectedYear]);

  useEffect(() => {
    const adjustLineWidth = () => {
      if (timelineRef.current && lineRef.current) {
        lineRef.current.style.width = `${timelineRef.current.scrollWidth}px`;
      }
    };

    adjustLineWidth();
    window.addEventListener("resize", adjustLineWidth);
    return () => window.removeEventListener("resize", adjustLineWidth);
  }, [laws]);

  const handleMoveTimeline = (direction: "prev" | "next") => {
    if (!selectedYear) return;

    const currentIndex = sortedYears.indexOf(selectedYear);
    const newIndex = direction === "prev" ? currentIndex - 1 : currentIndex + 1;

    if (newIndex >= 0 && newIndex < sortedYears.length) {
      setSelectedYear(sortedYears[newIndex]);
    }
  };

  const extractLawNumber = (
    norma?: string | null,
    nombre?: string | null
  ): number => {
    const s = `${norma ?? ""} ${nombre ?? ""}`;
    const matches = s.match(/\d+(?:[.,]\d+)*/g);
    if (!matches) return Number.NEGATIVE_INFINITY;
    return Math.max(
      ...matches.map((m) => parseInt(m.replace(/[.,]/g, ""), 10))
    );
  };

  const sortedLawsForYear = useMemo(() => {
    if (!selectedYear) return [];
    const list = laws[selectedYear] ?? [];
    return [...list].sort(
      (a, b) =>
        extractLawNumber(a.norma, a.nombre) -
        extractLawNumber(b.norma, b.nombre)
    );
  }, [laws, selectedYear]);

  const toggleShowAll = () => setShowAll(!showAll);

  return (
    <div className="md:rounded-sm md:shadow-custom-dual md:border md:border-system-light-60">
      <div className="md:p-8 relative">
        {/* Timeline Points */}
        <div className="timeline" ref={timelineRef}>
          <div className="middle-line" ref={lineRef}></div>
          {sortedYears.map((year, index) => (
            <TimelinePoint
              key={year}
              year={year}
              isActive={selectedYear === year}
              onClick={() => setSelectedYear(year)}
              ref={index === 0 ? itemRef : undefined}
            />
          ))}
        </div>
        <div
          className={`${
            selectedYear !== sortedYears[sortedYears.length - 1]
              ? "end-blur top-0 right-[-2px] md:top-[2rem] md:right-[2rem] w-[5.125rem] h-[100px]"
              : ""
          }`}
        ></div>

        {/* Timeline Controls */}
        <TimelineControls
          onPrev={() => handleMoveTimeline("prev")}
          onNext={() => handleMoveTimeline("next")}
          disablePrev={selectedYear === sortedYears[0]}
          disableNext={selectedYear === sortedYears[sortedYears.length - 1]}
        />

        {/* Law List */}
        {selectedYear && (
          <LawList
            laws={sortedLawsForYear}
            showAll={showAll}
            toggleShowAll={toggleShowAll}
          />
        )}
      </div>
    </div>
  );
};

export default Timeline;
